import React from 'react';
import { formatAmount } from '../../datasets/utils/formatters';

interface FeeBreakdown {
  merchant: {
    fixed: number;
    percentage: number;
    beforeCap: number;
    afterCap: number;
    capApplied: boolean;
  };
  bank: {
    fixed: number;
    percentage: number;
    beforeCap: number;
    afterCap: number;
    capApplied: boolean;
  };
  profit: number;
}

interface RevenueBreakdownProps {
  title: string;
  breakdown: FeeBreakdown;
  transactionCount: number;
  fixedFee: number;
  percentageFee: number;
  maxCap: number;
  bankFixedFee: number;
  bankPercentageFee: number;
  bankMaxCap: number;
}

export function RevenueBreakdown({
  title,
  breakdown,
  transactionCount,
  fixedFee,
  percentageFee,
  maxCap,
  bankFixedFee,
  bankPercentageFee,
  bankMaxCap
}: RevenueBreakdownProps) {
  return (
    <div className="card">
      <div className="px-5 py-3 border-b border-slate-100">
        <h3 className="section-title">{title}</h3>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <h4 className="text-[13px] font-medium text-slate-500 mb-2">Merchant Fees</h4>
          <div className="space-y-1.5 text-[13px]">
            <div className="flex justify-between">
              <span className="text-slate-500">Fixed Fee</span>
              <span className="font-medium text-slate-700">
                {formatAmount(fixedFee)} × {transactionCount} = {formatAmount(breakdown.merchant.fixed)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Percentage Fee ({percentageFee}%)</span>
              <span className="font-medium text-slate-700">{formatAmount(breakdown.merchant.percentage)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span className="text-slate-600">Total Before Cap</span>
              <span className="text-slate-900">{formatAmount(breakdown.merchant.beforeCap)}</span>
            </div>
            {breakdown.merchant.capApplied && (
              <div className="flex justify-between text-amber-600 text-[12px]">
                <span>Cap applied ({formatAmount(maxCap)}/txn)</span>
                <span>{formatAmount(breakdown.merchant.afterCap)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 border-t border-slate-100">
          <h4 className="text-[13px] font-medium text-slate-500 mb-2">Bank Fees</h4>
          <div className="space-y-1.5 text-[13px]">
            <div className="flex justify-between">
              <span className="text-slate-500">Fixed Fee</span>
              <span className="font-medium text-slate-700">
                {formatAmount(bankFixedFee)} × {transactionCount} = {formatAmount(breakdown.bank.fixed)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Percentage Fee ({bankPercentageFee}%)</span>
              <span className="font-medium text-slate-700">{formatAmount(breakdown.bank.percentage)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span className="text-slate-600">Total Before Cap</span>
              <span className="text-slate-900">{formatAmount(breakdown.bank.beforeCap)}</span>
            </div>
            {breakdown.bank.capApplied && (
              <div className="flex justify-between text-amber-600 text-[12px]">
                <span>Cap applied ({formatAmount(bankMaxCap)}/txn)</span>
                <span>{formatAmount(breakdown.bank.afterCap)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 border-t border-slate-100 flex justify-between items-baseline">
          <span className="text-[14px] font-semibold text-slate-700">Net Profit</span>
          <span className="text-lg font-bold text-emerald-600">{formatAmount(breakdown.profit)}</span>
        </div>
      </div>
    </div>
  );
}
